import { useEffect, useState } from 'react';
import { useTranslation } from '../../../../i18n/useTranslation';
import { useBulkLicenseActions } from '../../../../hooks/useBulkLicenseActions';
import type { ManageLicenseListDto, BulkLicenseActionResultDto } from '../../../../types/manageLicenses';

interface Props {
  open: boolean;
  licenses: ManageLicenseListDto[];
  onClose: () => void;
  onCompleted: (result: BulkLicenseActionResultDto) => void;
}

export default function LicenseRevokeDialog({ open, licenses, onClose, onCompleted }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [challenge, setChallenge] = useState('');
  const { revoke } = useBulkLicenseActions();

  useEffect(() => {
    if (!open) return;
    setReason('');
    setChallenge('');
  }, [open]);

  if (!open) return null;

  const requiresChallenge = licenses.length >= 10;
  const challengeSatisfied = !requiresChallenge || challenge === 'REVOKE';
  const canSubmit = reason.trim().length > 0 && challengeSatisfied && !revoke.isPending;

  const submit = async () => {
    const result = await revoke.mutateAsync({
      licenseIds: licenses.map((l) => l.id),
      reason: reason.trim(),
    });
    onCompleted(result);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
        <h2 className="text-lg font-semibold mb-2 text-red-700">
          {t('App.Admin.Licenses.LicenseRevokeDialog.Title', { count: licenses.length })}
        </h2>
        <p className="text-sm text-neutral-700 mb-4">
          {t('App.Admin.Licenses.LicenseRevokeDialog.Description')}
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            {t('App.Admin.Licenses.LicenseRevokeDialog.ReasonLabel')} <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
            rows={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('App.Admin.Licenses.LicenseRevokeDialog.ReasonPlaceholder')}
          />
          <p className="text-xs text-neutral-500 text-right mt-0.5">{t('App.Admin.Licenses.LicenseRevokeDialog.CharCount', { count: reason.length })}</p>
        </div>

        {requiresChallenge && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('App.Admin.Licenses.LicenseRevokeDialog.ChallengeLabel')}
            </label>
            <input
              type="text"
              className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              placeholder={t('App.Admin.Licenses.LicenseRevokeDialog.ChallengePlaceholder')}
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100"
          >
            {t('App.Admin.Licenses.LicenseRevokeDialog.Cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {revoke.isPending ? t('App.Admin.Licenses.LicenseRevokeDialog.Revoking') : t('App.Admin.Licenses.LicenseRevokeDialog.Revoke')}
          </button>
        </div>
      </div>
    </div>
  );
}
